import { PartnerName } from 'configs/constants';
import envConfig from 'configs/envConfig';
import { storedLocale } from 'partners/storedLocale';
import { assets as haloviewAssets } from './haloview/assets';
import { assets as homiqAssets } from './homiq/assets';
import { assets as instaviewAssets } from './instaview/assets';
import { assets as lunaAssets } from './luna/assets';
import { assets as newaieyeAssets } from './newaieye/assets';
import {
  TAllPartnerAssets,
  TCommonPartnerAssets,
  TPartnerAssets
} from './types';
import { assets as waiscenAssets } from './waiscen/assets';

const partnerAssetsMap = {
  [PartnerName.INSTAVIEW]: instaviewAssets,
  [PartnerName.LUNA]: lunaAssets,
  [PartnerName.NEWAIEYE]: newaieyeAssets,
  [PartnerName.HOMIQ]: homiqAssets,
  [PartnerName.HALOVUE]: haloviewAssets,
  [PartnerName.WAISCEN]: waiscenAssets,
  // Sandbox tenant of instaview — same B2 asset bucket path.
  [PartnerName.IV_SANDBOX]: instaviewAssets,
  default: instaviewAssets
};

const PARTNER = envConfig('partner') || 'instaview';

// const baseUrl = `${import.meta.env.VITE_B2_BUCKET_URL}/homeSecurity/${PARTNER}`;

const commonUrl = `${envConfig('b2BucketUrl')}/homeSecurity/common`;

export const defaultCommonAssets: TCommonPartnerAssets = {
  support24Icon: `${commonUrl}/24-support.svg`,
  sparkleIcon: `${commonUrl}/sparkleAIIcon.png`,
  invoiceIcon: `${commonUrl}/Invoice.svg`,
  aiFalseAlertIcon: `${commonUrl}/ai_false_alert.svg`,
  alarmIcon: `${commonUrl}/alarm.svg`,
  amexCard: `${commonUrl}/amex.svg`,
  camera1Icon: `${commonUrl}/cameraIcon.png`,
  cameraSnapshotPlaceholderImg: `${commonUrl}/cameraSnapshotPlaceholder.png`,
  camera2Icon: `${commonUrl}/camera_icon.svg`,
  cloudAddIcon: `${commonUrl}/cloud-add.svg`,
  cloudStorageIcon: `${commonUrl}/cloud-storage.svg`,
  contextualAIIcon: `${commonUrl}/contextualAI.svg`,
  creditRegularCard: `${commonUrl}/credit-card-regular.svg`,
  defaultScreenshotImg: `${commonUrl}/defaultScreenshot.png`,
  desktopAccessImg: `${commonUrl}/desktop-access-illustration.png`,
  discoverCard: `${commonUrl}/discover.png`,
  drawerMenuIcon: `${commonUrl}/drawerMenuIcon.svg`,
  eyeIcon: `${commonUrl}/eye.svg`,
  infoIcon: `${commonUrl}/infoIcon.svg`,
  masterCard: `${commonUrl}/mastercard.svg`,
  monitoringIcon: `${commonUrl}/monitoringIcon.svg`,
  monitoringCertificateIcon: `${commonUrl}/monitoring_certificate.svg`,
  notificationBingIcon: `${commonUrl}/notification-bing.svg`,
  peopleIcon: `${commonUrl}/people_icon.svg`,
  professionalMonitorEyeIcon: `${commonUrl}/professional_monitor_eye.svg`,
  searchIcon: `${commonUrl}/searchIcon.svg`,
  videoCameraIcon: `${commonUrl}/videoCamera.svg`,
  videoCamera10Icon: `${commonUrl}/video_10_camera.svg`,
  videoCamera1Icon: `${commonUrl}/video_1_camera.svg`,
  visaCard: `${commonUrl}/visa.svg`,
  timelapseIcon: `${commonUrl}/timelapseIcon.svg`,
  defaultIcon: `${commonUrl}/star.svg`,
  default4GScreenshotImg: `${commonUrl}/fourGDefaultSnapshot.png`,
  fourGPromoLiveView: `${commonUrl}/liveViewOnly.png`,
  fourGPromoNoAIAlerts: `${commonUrl}/fourGNoContextualAlerts.png`,
  fourGMotionAlerts: `${commonUrl}/motionNotifications.png`,
  fourGLimitiedSecurity: `${commonUrl}/fourGLimitedSecurity.png`,
  fourGSnapshot: `${commonUrl}/fourGSnapshot.png`,
  fourGExpensiveData: `${commonUrl}/fourGExpensiveData.png`,
  thirtyDayStorageVideo: `${commonUrl}/cloud-storage.mp4`,
  descriptiveAlertVideo: `${commonUrl}/contextual-alerts.mp4`,
  videoAlarmDetectionVideo: `${commonUrl}/pro-monitoring.mp4`,
  promotionMonitoringImg: `${commonUrl}/promotion-professional-monitoring.jpg`,
  promotionSmartAiImg: `${commonUrl}/promotion-smart-ai.jpg`,
  promotionFaceRecognitionImg: `${commonUrl}/promotion-face-recognition.jpg`,
  promotionDescriptiveAlertsImg: `${commonUrl}/promotion-descriptive-alerts.jpg`,
  promotionCloudStorageImg: `${commonUrl}/promotion-cloud-storage.jpg`,
  noEventVideosImg: `${commonUrl}/noEventVideos.png`,
  limitedSecurityImg: `${commonUrl}/limitedSecurity.png`,
  promoExpensiveDataVideoId: `${commonUrl}/promo4GData.mp4`,
  autoDataRechargeEnabled: `${commonUrl}/autoRechargeEnabled.png`,
  aiIntelligent: `${commonUrl}/aiIntelligent.png`,
  cloudStorage: `${commonUrl}/cloudStorage.png`,
  fourGdata: `${commonUrl}/fourGdata.png`,
  desktopAccessIcon: `${commonUrl}/desktop-access-icon.svg`,
  noDesktopAccessIcon: `${commonUrl}/no-desktop-access-icon.png`,
  noEventsPromo: `${commonUrl}/eventsPromo.png`
};

// 2️⃣ Locale-specific overrides
const localeOverrides: Record<string, Partial<TCommonPartnerAssets>> = {
  es: {
    noEventVideosImg: `${commonUrl}/noEventVideos-es.png`
  }
};

// 3️⃣ Merge default with overrides
export const getAssetsForLocale = (locale: string): TCommonPartnerAssets => {
  return {
    ...defaultCommonAssets,
    ...localeOverrides[locale]
  };
};

const locale = storedLocale();

export const commonAssets = getAssetsForLocale(locale || 'en-US');

const getPartnerAssetConfig = (): TPartnerAssets => {
  return (
    partnerAssetsMap[PARTNER as keyof typeof partnerAssetsMap] ||
    partnerAssetsMap.default
  );
};

// Was rebuilt via `Object.entries(...).reduce(...)` into a fresh object —
// a pure identity copy (every key carried straight through unchanged), but
// typed as `Record<keyof TPartnerAssets, string>`, which assumed every
// field was a string. That broke once `logoMarkIcon` (a component, not a
// string) was added. `assetConfig` is already correctly typed as
// `TPartnerAssets`, so this is just that value directly.
export const partnerSpecificAssets: TPartnerAssets = getPartnerAssetConfig();

export const partnerAssets: TAllPartnerAssets = {
  ...partnerSpecificAssets,
  ...commonAssets
};
